import { useCallback } from 'react';

import {
  ISingularAttributionInput,
  PartnerLinkSource,
  useInitiateLinkingMutation,
} from 'generated/graphql-gateway';

import { appTokenAuthContext } from './app-token';

/**
 * Wraps the THLE-4018 `initiateLinking` Apollo mutation so the entry-page
 * CTA gets a single `initiateLinking({ partnerId, source })` Promise that
 * resolves to `{ oidcAuthUrl }` (the base CT authorize URL — RP appends
 * PKCE `code_challenge` / `_method` / `state` before redirecting).
 *
 * Failure contract: the returned `initiateLinking` Promise rejects on either
 * a transport/GraphQL error or a missing `oidcAuthUrl` in the response, so
 * callers should `try/catch` and surface UI from the rejection. We do not
 * mirror the error onto the hook surface — `useInitiateLinkingMutation`
 * exposes its own lagged `error` state if a consumer ever needs it.
 */

export interface IInitiateLinkingResult {
  oidcAuthUrl: string;
}

export interface IInitiateLinkingVariables {
  partnerId: string;
  source: PartnerLinkSource;
  // App-handoff JWT (from the URL hash). When present it authenticates the
  // mutation as Bearer, since the browser has no TH Cognito session.
  appToken?: string | null;
  // Campaign identifiers the guest arrived on. Recorded on the INITIATED row so
  // an attempt that never completes still carries the campaign that drove it —
  // that row is the denominator for drop-off. Omitted entirely when absent.
  singularAttribution?: ISingularAttributionInput;
}

export interface IUseInitiateLinking {
  initiateLinking: (variables: IInitiateLinkingVariables) => Promise<IInitiateLinkingResult>;
  loading: boolean;
}

export const useInitiateLinking = (): IUseInitiateLinking => {
  const [mutate, { loading }] = useInitiateLinkingMutation();

  const initiateLinking = useCallback(
    async ({
      partnerId,
      source,
      appToken,
      singularAttribution,
    }: IInitiateLinkingVariables): Promise<IInitiateLinkingResult> => {
      const result = await mutate({
        variables: { partnerId, source, singularAttribution },
        context: appToken ? appTokenAuthContext(appToken) : undefined,
      });
      const oidcAuthUrl = result.data?.initiateLinking?.oidcAuthUrl;
      if (!oidcAuthUrl) {
        throw new Error('initiateLinking returned no oidcAuthUrl');
      }
      return { oidcAuthUrl };
    },
    [mutate]
  );

  return { initiateLinking, loading };
};
